<script setup lang="ts">
import type { ServiceGender } from "~/types";

const { t } = useI18n();
const route = useRoute();
const { selectedLocation } = storeToRefs(useBusinessStore());

const isSuppressedRoute = computed(() => {
  const keys = (route.meta.keys as string[] | undefined) ?? [];
  if (keys.includes("booking_confirmation")) return true;
  if (keys.includes("booking_complete")) return true;
  if (route.meta.title === "booking_details") return true;
  return false;
});

const {
  showSectionSelector,
  isSelectorOpen,
  selectedGender,
  hasPrompted,
  hasBothGenderEmployees,
  markPrompted,
  openSelector,
  closeSelector,
  setGender,
} = useServiceGender();

const open = computed({
  get: () =>
    showSectionSelector.value &&
    isSelectorOpen.value &&
    !isSuppressedRoute.value,
  set: (value: boolean) => {
    isSelectorOpen.value = value;
  },
});

const options = computed<
  { value: ServiceGender | null; icon: string; label: string }[]
>(() => {
  const items: { value: ServiceGender | null; icon: string; label: string }[] = [];
  if (hasBothGenderEmployees.value) {
    items.push({ value: null, icon: "i-approved-group", label: t("service_gender.both") });
  }
  items.push({ value: "male", icon: "i-mdi-human-male", label: t("service_gender.male") });
  items.push({ value: "female", icon: "i-mdi-human-female", label: t("service_gender.female") });
  return items;
});

const choose = (value: ServiceGender | null) => {
  setGender(value);
  markPrompted();
  closeSelector();
};

const maybeAutoOpen = () => {
  if (isSuppressedRoute.value) return;
  if (showSectionSelector.value && !hasPrompted.value) {
    openSelector();
  }
};

onMounted(maybeAutoOpen);
watch(() => selectedLocation.value?.identifier, maybeAutoOpen);
watch(() => route.fullPath, maybeAutoOpen);
</script>

<template>
  <CommonBottomSheet
    v-model:open="open"
    fit-content
    :title="t('service_gender.title')">
    <div class="space-y-3 px-4 xs:px-5 pb-6 xs:pb-8">
      <div>
        <h2 class="text-base font-semibold text-gray-900 ltr:first-letter:uppercase">
          {{ t("service_gender.title") }}
        </h2>
        <p class="text-sm text-gray-500 ltr:first-letter:uppercase mt-1">
          {{
            hasBothGenderEmployees
              ? t("service_gender.desc", { name: selectedLocation?.name })
              : t("service_gender.desc_no_both", { name: selectedLocation?.name })
          }}
        </p>
      </div>

      <div class="flex flex-col gap-3">
        <button
          v-for="option in options"
          :key="option.label"
          type="button"
          class="flex items-center bg-white p-4 pe-6 rounded-2xl gap-x-3 overflow-hidden cursor-pointer transition-all duration-300 text-start ring-1 ring-inset"
          :class="
            selectedGender === option.value
              ? 'ring-primary ring-2'
              : 'ring-gray-200 hover:ring-primary-400'
          "
          @click="choose(option.value)">
          <div
            class="shrink-0 flex size-12 items-center justify-center rounded-xl bg-gray-100">
            <UIcon :name="option.icon" class="size-6 text-primary" />
          </div>
          <div class="flex flex-col flex-1 min-w-0">
            <p class="text-gray-900 first-letter:uppercase">{{ option.label }}</p>
          </div>
          <UIcon
            v-if="selectedGender === option.value"
            name="i-heroicons-check-20-solid"
            class="shrink-0 size-5 text-primary" />
        </button>
      </div>
    </div>
  </CommonBottomSheet>
</template>
